import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Col, Grid } from 'antd';

import CatalogBanner from './CatalogBanner';
import { buildBreakpointStyles } from './CatalogBannerRow';
import { Banner } from '@eshop/types/banners.types.ts';
import type { TemplateGridSpan } from '@eshop/templates';
import type { TemplateGridBreakpointConfig } from '@eshop/templates/system/templateTypes';
import { useCatalogueDataContext } from './context';
import { useCatalogueTemplate } from '@eshop/templates/TemplateContext';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import CatalogueSocialBar from './CatalogueSocialBar';
import { useAppSelector } from '@store/store.tsx';
import { selectSiteSettings } from '@store/slices/settingsSlice.tsx';
import { buildCatalogueSocialLinks } from '@components/layout/catalogueSocial.tsx';
import { resolveCatalogueSocialInsertIndex } from './catalogueSocialPlacement';
import { useOptionalCatalogueAppearance } from '@eshop/designs';
import {
  resolveActiveCatalogueColumnCount,
  resolveBetweenRowsInsertIndex,
} from './catalogueRowPlacement';

const CAROUSEL_INTERVAL_MS = 8000;

const FALLBACK_BANNER_BREAKPOINTS: TemplateGridSpan = { xs: { span: 24 } };

interface CatalogBannerCarouselProps {
  banners: Banner[];
  breakpoints: TemplateGridSpan;
  locationKey: string;
  aspectRatio?: string;
}

const CatalogBannerCarousel = ({
  banners,
  breakpoints,
  locationKey,
  aspectRatio,
}: CatalogBannerCarouselProps) => {
  const total = banners.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const colStyle = useMemo(() => buildBreakpointStyles(breakpoints), [breakpoints]);

  const wrapIndex = useCallback(
    (index: number) => {
      if (total === 0) {
        return 0;
      }
      const mod = index % total;
      return mod < 0 ? mod + total : mod;
    },
    [total],
  );

  const goTo = useCallback(
    (index: number) => {
      setActiveIndex(wrapIndex(index));
    },
    [wrapIndex],
  );

  const handleNext = useCallback(() => {
    goTo(activeIndex + 1);
  }, [activeIndex, goTo]);

  const handlePrev = useCallback(() => {
    goTo(activeIndex - 1);
  }, [activeIndex, goTo]);

  const handleDotClick = useCallback(
    (index: number) => {
      goTo(index);
    },
    [goTo],
  );

  useEffect(() => {
    if (typeof window === 'undefined' || total <= 1 || isPaused) {
      return undefined;
    }

    const id = window.setInterval(() => {
      setActiveIndex((prev) => wrapIndex(prev + 1));
    }, CAROUSEL_INTERVAL_MS);

    return () => {
      window.clearInterval(id);
    };
  }, [isPaused, total, wrapIndex]);

  if (total === 0) {
    return null;
  }

  const activeBanner = banners[activeIndex];

  return (
    <Col
      // className="catalogue-panel__col catalogue-panel__col--banner"
      className="catalogue-panel__col--banner"
      style={colStyle}
      xs={24}
    >
      <div
        className="catalog-banner-carousel"
        role="group"
        aria-roledescription="Carrusel de banners"
        aria-label="Banners destacados"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocusCapture={() => setIsPaused(true)}
        onBlurCapture={() => setIsPaused(false)}
      >
        <CatalogBanner
          key={`${locationKey}-${activeBanner.id}`}
          banner={activeBanner}
          breakpoints={breakpoints}
          renderAsChild
          aspectRatio={aspectRatio}
          className="catalog-banner-carousel__banner"
        />
        {total > 1 ? (
          <>
            <div className="catalog-banner-carousel__nav">
              <button
                type="button"
                className="catalog-banner-carousel__button catalog-banner-carousel__button--prev"
                aria-label="Banner anterior"
                onClick={handlePrev}
              >
                <LeftOutlined />
              </button>
              <button
                type="button"
                className="catalog-banner-carousel__button catalog-banner-carousel__button--next"
                aria-label="Banner siguiente"
                onClick={handleNext}
              >
                <RightOutlined />
              </button>
            </div>
            <div className="catalog-banner-carousel__dots" role="tablist" aria-label="Seleccionar banner">
              {banners.map((banner, index) => {
                const isActive = index === activeIndex;
                return (
                  <button
                    key={`${locationKey}-dot-${banner.id}`}
                    type="button"
                    className={
                      isActive
                        ? 'catalog-banner-carousel__dot catalog-banner-carousel__dot--active'
                        : 'catalog-banner-carousel__dot'
                    }
                    aria-label={`Mostrar banner ${index + 1} de ${total}`}
                    aria-pressed={isActive}
                    onClick={() => handleDotClick(index)}
                  />
                );
              })}
            </div>
          </>
        ) : null}
      </div>
    </Col>
  );
};

const normalizeBannerBreakpoints = (breakpoints?: TemplateGridSpan): TemplateGridSpan => {
  if (!breakpoints) {
    return FALLBACK_BANNER_BREAKPOINTS;
  }

  const entries = Object.entries(breakpoints).filter(([, value]) => value !== undefined && value !== null);

  if (entries.length === 0) {
    return FALLBACK_BANNER_BREAKPOINTS;
  }

  return entries.reduce<TemplateGridSpan>((acc, [key, value]) => {
    if (typeof value === 'number') {
      acc[key as keyof TemplateGridSpan] = value;
    } else if (typeof value === 'object') {
      acc[key as keyof TemplateGridSpan] = { ...value };
    }

    return acc;
  }, {} as TemplateGridSpan);
};

type CatalogueGridConfig = ReturnType<typeof useCatalogueDataContext>['gridConfig'];

const parseColumnsFromSpan = (span?: number, fallback?: number): number | undefined => {
  if (typeof span !== 'number' || span <= 0) {
    return fallback;
  }

  const computed = Math.floor(24 / span);
  return Number.isFinite(computed) && computed > 0 ? computed : fallback;
};

const FLEX_PERCENTAGE_REGEX = /([\d.]+)%$/;

const parseColumnsFromFlex = (
  flex?: string | number,
  fallback?: number,
): number | undefined => {
  if (typeof flex === 'number') {
    if (flex <= 0) {
      return fallback;
    }

    const computed = Math.round(1 / flex);
    return Number.isFinite(computed) && computed > 0 ? computed : fallback;
  }

  if (typeof flex !== 'string') {
    return fallback;
  }

  const match = FLEX_PERCENTAGE_REGEX.exec(flex.trim());
  if (!match) {
    return fallback;
  }

  const percentage = Number.parseFloat(match[1]);
  if (!Number.isFinite(percentage) || percentage <= 0) {
    return fallback;
  }

  const computed = Math.round(100 / percentage);
  return computed > 0 ? computed : fallback;
};

const deriveColumnsFromBreakpoint = (
  value: number | TemplateGridBreakpointConfig | undefined,
  fallback?: number,
): number | undefined => {
  if (typeof value === 'number') {
    return parseColumnsFromSpan(value, fallback);
  }

  if (!value) {
    return fallback;
  }

  const spanColumns = parseColumnsFromSpan(value.span, fallback);
  if (spanColumns) {
    return spanColumns;
  }

  return parseColumnsFromFlex(value.flex, fallback);
};

export const getVisibleColumns = (
  config?: CatalogueGridConfig,
): Partial<Record<keyof TemplateGridSpan, number>> => {
  if (!config) {
    return {};
  }

  if (config.columns) {
    const normalized = Object.entries(config.columns).reduce<
      Partial<Record<keyof TemplateGridSpan, number>>
    >((acc, [breakpoint, value]) => {
      if (typeof value === 'number' && value > 0) {
        acc[breakpoint as keyof TemplateGridSpan] = value;
      }
      return acc;
    }, {});

    if (Object.keys(normalized).length > 0) {
      return normalized;
    }
  }

  if (!config.breakpoints) {
    return {};
  }

  return (Object.entries(config.breakpoints) as Array<[
    keyof TemplateGridSpan,
    number | TemplateGridBreakpointConfig,
  ]>).reduce<Partial<Record<keyof TemplateGridSpan, number>>>((acc, [breakpoint, value]) => {
    const columns = deriveColumnsFromBreakpoint(value);
    if (columns) {
      acc[breakpoint] = columns;
    }

    return acc;
  }, {});
};

const resolveBetweenInsertIndexImpl = resolveBetweenRowsInsertIndex;
const resolveActiveColumnCountImpl = resolveActiveCatalogueColumnCount;

export const bannerSlotInternals = {
  resolveBetweenInsertIndex: resolveBetweenInsertIndexImpl,
  resolveActiveColumnCount: resolveActiveColumnCountImpl,
};

export const resolveBetweenInsertIndex = (
  itemsLength: number,
  columns: number,
): number | null => bannerSlotInternals.resolveBetweenInsertIndex(itemsLength, columns);

const appendBottomBanners = (
  baseItems: React.ReactNode[],
  bottomBanners: React.ReactNode[],
): React.ReactNode[] => {
  if (bottomBanners.length === 0) {
    return baseItems;
  }

  return [...baseItems, ...bottomBanners];
};

interface Props {
  banners: Banner[];
  items?: React.ReactNode[];
  /** When true, top banners will not be rendered in the grid (they should be rendered elsewhere) */
  hideTopBanners?: boolean;
  /** Renders only catalog:top banners, preserving the same carousel behavior used by the grid slot. */
  topOnly?: boolean;
}

// renders banners depending on their locations
const BannerSlots = ({ banners, items = [], hideTopBanners = false, topOnly = false }: Props) => {
  const { gridConfig } = useCatalogueDataContext();
  const screens = Grid.useBreakpoint();
  const settings = useAppSelector(selectSiteSettings);
  const appearance = useOptionalCatalogueAppearance();
  const template = useCatalogueTemplate();
  const aspectRatio = template.bannerAspectRatio || 'aspect_5-1';
  const gridBreakpoints = gridConfig?.breakpoints;
  const bannerBreakpoints = useMemo(
    () => normalizeBannerBreakpoints(gridBreakpoints),
    [gridBreakpoints],
  );

  const groupedBanners = useMemo(() => {
    const topBanners: Banner[] = [];
    const betweenBanners: Banner[] = [];
    const bottomBanners: Banner[] = [];

    banners.forEach((banner) => {
      if (banner.locations.includes('catalog:top')) {
        topBanners.push(banner);
      }

      if (banner.locations.includes('catalog:betweenRows')) {
        betweenBanners.push(banner);
      }

      if (banner.locations.includes('catalog:bottom')) {
        bottomBanners.push(banner);
      }
    });

    return { topBanners, betweenBanners, bottomBanners };
  }, [banners]);

  const { topBanners, betweenBanners, bottomBanners } = groupedBanners;

  const renderBannerGroup = useCallback(
    (group: Banner[], prefix: 'top' | 'mid' | 'bot'): ReactNode[] => {
      if (group.length === 0) {
        return [];
      }

      if (group.length === 1) {
        const [banner] = group;
        return [
          <CatalogBanner
            key={`${prefix}-${banner.id}`}
            banner={banner}
            breakpoints={bannerBreakpoints}
            aspectRatio={aspectRatio}
          />,
        ];
      }

      return [
        <CatalogBannerCarousel
          key={`${prefix}-carousel`}
          banners={group}
          breakpoints={bannerBreakpoints}
          locationKey={prefix}
          aspectRatio={aspectRatio}
        />,
      ];
    },
    [bannerBreakpoints, aspectRatio],
  );

  const topNodes = useMemo(
    () => (hideTopBanners ? [] : renderBannerGroup(topBanners, 'top')),
    [renderBannerGroup, topBanners, hideTopBanners],
  );

  const betweenNodes = useMemo(
    () => renderBannerGroup(betweenBanners, 'mid'),
    [renderBannerGroup, betweenBanners],
  );

  const bottomNodes = useMemo(
    () => renderBannerGroup(bottomBanners, 'bot'),
    [renderBannerGroup, bottomBanners],
  );

  const visibleColumns = useMemo(() => getVisibleColumns(gridConfig), [gridConfig]);
  const activeColumnCount = useMemo(() => {
    return resolveActiveColumnCountImpl(visibleColumns, screens, appearance?.design);
  }, [appearance?.design, screens, visibleColumns]);
  const hasSocialLinks = buildCatalogueSocialLinks(settings).length > 0;
  const socialInsertIndex = hasSocialLinks
    ? resolveCatalogueSocialInsertIndex(items.length, activeColumnCount)
    : null;
  const itemsWithSocial = useMemo(() => {
    if (socialInsertIndex === null) return items;
    return [
      ...items.slice(0, socialInsertIndex),
      <CatalogueSocialBar key="catalogue-social-bar" />,
      ...items.slice(socialInsertIndex),
    ];
  }, [items, socialInsertIndex]);

  const betweenIdsKey = useMemo(
    () => betweenBanners.map((banner) => banner.id).join('|'),
    [betweenBanners],
  );

  const insertIndex = useMemo(
    () => resolveBetweenInsertIndex(items.length, activeColumnCount),
    [items.length, activeColumnCount, betweenIdsKey],
  );

  const { itemsWithBetween, bottomCandidates } = useMemo(() => {
    if (betweenNodes.length === 0) {
      return { itemsWithBetween: itemsWithSocial, bottomCandidates: bottomNodes };
    }

    if (insertIndex === null) {
      return {
        itemsWithBetween: itemsWithSocial,
        bottomCandidates: [...betweenNodes, ...bottomNodes],
      };
    }

    const adjustedInsertIndex =
      socialInsertIndex !== null && insertIndex >= socialInsertIndex
        ? insertIndex + 1
        : insertIndex;
    const clampedIndex = Math.min(adjustedInsertIndex, itemsWithSocial.length);
    const before = itemsWithSocial.slice(0, clampedIndex);
    const after = itemsWithSocial.slice(clampedIndex);

    return {
      itemsWithBetween: [...before, ...betweenNodes, ...after],
      bottomCandidates: bottomNodes,
    };
  }, [itemsWithSocial, betweenNodes, bottomNodes, insertIndex, socialInsertIndex]);

  const itemsWithBottom = useMemo(
    () => appendBottomBanners(itemsWithBetween, bottomCandidates),
    [itemsWithBetween, bottomCandidates],
  );

  if (topOnly) {
    return <>{topNodes}</>;
  }

  return <>{[...topNodes, ...itemsWithBottom]}</>;
};

export default BannerSlots;
